import { simpleLerp } from '../../../lib/math';
import type { TimeData } from '../../core/TimeData';
import {
  isPortCallArrivalEvent,
  isPortCallDepartureEvent,
} from '../../core/TimeData';
import { memoize } from 'lodash';
import type {
  ScenarioTimeline_ShipFragment,
  VesselDraftDatapointFragment,
} from '../../../graphql';

/**
 * When cargo load on the vessel is more than
 * maxDraftAtLocation * OVERDRAFT_THRESHOLD we display
 * a yellow warning;
 * When it is more than maxDraftAtLocation we display
 * a red warning
 */
const OVERDRAFT_THRESHOLD = 0.95;

/**
 *  The severity of vessel overdraft
 *  'warning' if (max-draft-at-port * OVERDRAFT_THRESHOLD < vessel-draft < max-draft-at-port)
 *  'error' if (max-draft-at-port < vessel-draft)
 */
export type OverDraftWarningSeverity = 'warning' | 'error';

/**
 *  Extracts vessel draft data points for estimation of an actual draft
 *  of the vessel at a given cargo load.
 *  @param nodes
 *  @returns an array of draft datapoints, where each datapoint
 *  is a tuple of `[cargoLoad, draftAtLoad]:[number, number]`
 */
const getDraftDatapointsFromNodes = (
  nodes: Array<VesselDraftDatapointFragment | null>,
): Array<[number, number]> => {
  if (!nodes || nodes.length === 0) return [];

  return nodes.reduce(
    (acc, data) => {
      if (!data) return acc;

      const { cargoLoad, draft } = data;

      if (!cargoLoad || !draft) return acc;

      acc.push([Number(cargoLoad), Number(draft)]);
      return acc;
    },
    [] as Array<[number, number]>,
  );
};

/**
 * Calculates the line segment containing specified cargoLoad from a set
 * of vessel datapoints.
 *
 * E.g. given a set of `[[1, 3], [3, 6], [7, 10]]` and `cargoLoad` of `4`
 * it will return line segment `[[3, 6], [7, 10]]`
 */
export const getDraftDatapointSegmentFromLoad = function <
  T extends [number, number],
>({
  draftDatapoints: draftDPs,
  cargoLoad,
}: {
  draftDatapoints: Array<T>;
  cargoLoad: number;
}): [T, T] | undefined {
  const draftDatapoints = draftDPs.sort((a, b) => a[0] - b[0]);
  if (draftDatapoints.length <= 1) return undefined;

  if (cargoLoad <= draftDatapoints[0][0]) {
    return draftDatapoints.slice(0, 2) as [T, T];
  }
  if (cargoLoad >= draftDatapoints[draftDatapoints.length - 1][0]) {
    return draftDatapoints.slice(
      draftDatapoints.length - 2,
      draftDatapoints.length,
    ) as [T, T];
  }

  for (let i = 0; i < draftDatapoints.length - 1; i++) {
    const currentPoint = draftDatapoints[i];
    const nextPoint = draftDatapoints[i + 1];

    if (cargoLoad >= currentPoint[0] && cargoLoad < nextPoint[0]) {
      return [currentPoint, nextPoint];
    }
  }
  return undefined;
};

type DraftEstimationData = { usedMassCapacity: number; maxPortDraft: number };
const UNDEFINED_DRAFT_ESTIMATION_DATA: Partial<DraftEstimationData> = {
  usedMassCapacity: undefined,
  maxPortDraft: undefined,
};
/**
 *  Checks incoming timedata to make sure it represents either
 *  departure or arrival port call event,
 *  extracts data used to estimate actual draft of the vessel at the event
 */
const getDraftEstimationDataFromTimeData = memoize(function (
  td: TimeData | undefined,
): Partial<DraftEstimationData> {
  if (
    !td ||
    !td.event ||
    (!isPortCallArrivalEvent(td.event) &&
      !isPortCallDepartureEvent(td.event)) ||
    !td.event.location ||
    !td.event.location.draft
  )
    return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const { usedMassCapacity } = td;

  // Skip if there is no cargo load
  if (usedMassCapacity === 0) return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const maxPortDraft = Number(td.event.location.draft);
  return { usedMassCapacity, maxPortDraft };
});

export type EventDraftIssue = {
  draftIssueSeverity: OverDraftWarningSeverity;
  estimatedVesselDraft: number;
  portMaxDraft: number;
};

/**
 * Calculates the severity of vessel overdraft at a port based on params.
 *
 * @param coefficients for linear regression interpolation
 * @param portMaxDraft max allowed draft at a given port
 * @param usedMassCapacity used mass capacity of the vessel
 * @returns `EventDraftIssues` object, or undefined if no overdraft was detected
 * The resulting object contains data on the estimated vessel draft and max allowed
 * draft at location for ease of display. The severity of the warning works as follows:
 *  - 'warning' if vessel draft almost exceeds max draft at port, according to `OVERDRAFT_THRESHOLD`;
 *  - 'error' if vessel draft exceeds max draft at port
 *  - `undefined` if no overdraft was detected
 */
const getPortDraftWarningInfo = function (
  estimatedVesselDraft: number,
  portMaxDraft: number,
): EventDraftIssue | undefined {
  let draftIssueSeverity: OverDraftWarningSeverity | undefined = undefined;
  if (estimatedVesselDraft - Number(portMaxDraft) * OVERDRAFT_THRESHOLD > 0) {
    draftIssueSeverity = 'warning';
  }
  if (estimatedVesselDraft > Number(portMaxDraft)) {
    draftIssueSeverity = 'error';
  }
  if (!draftIssueSeverity) return undefined;
  return {
    draftIssueSeverity,
    portMaxDraft: Number(portMaxDraft),
    estimatedVesselDraft,
  };
};

const isSafeDraftEstimationData = (
  data: Partial<DraftEstimationData>,
): data is DraftEstimationData =>
  data.maxPortDraft !== undefined && data.usedMassCapacity !== undefined;

/**
 * Gets a vessel draft warning for a given event, if any.
 */
export const getDraftWarningAtEvent = memoize(
  (event: TimeData): EventDraftIssue | undefined => {
    const draftEstimationData = event
      ? getDraftEstimationDataFromTimeData(event)
      : UNDEFINED_DRAFT_ESTIMATION_DATA;
    const estimatedDraft =
      event?.event?.vessel && isSafeDraftEstimationData(draftEstimationData)
        ? estimateActualVesselDraft(event.event.vessel)(draftEstimationData)
        : undefined;
    if (draftEstimationData.maxPortDraft && estimatedDraft)
      return getPortDraftWarningInfo(
        estimatedDraft,
        draftEstimationData.maxPortDraft,
      );
    return undefined;
  },
);

/**
 * Calculates estimated draft of a vessel, given its used mass capacity.
 * Uses vessel draft datapoints to perform the estimation, if no datapoints
 * are available returns `vessel.draft` as a fallback value.
 *
 * @param vessel
 * @returns estimated vessel draft or null
 */
const estimateActualVesselDraft = memoize(
  (vessel: ScenarioTimeline_ShipFragment) => {
    const draftValueFallback = vessel.draft ? Number(vessel.draft) : undefined;
    const draftDatapoints = getDraftDatapointsFromNodes(
      vessel.vesselDraftDatapointsByShipId.nodes,
    );
    return memoize(({ usedMassCapacity }: { usedMassCapacity: number }) => {
      const datapointSegment = getDraftDatapointSegmentFromLoad({
        draftDatapoints,
        cargoLoad: usedMassCapacity,
      });
      if (!datapointSegment) return draftValueFallback;
      try {
        return simpleLerp(usedMassCapacity, datapointSegment);
      } catch (_) {
        return draftValueFallback;
      }
    });
  },
);
